import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SigningHint from "features/signing-hint/signing-hint";
import type { CustomerFinance } from "types/Customer";
import type { FileEntity } from "types/Files";
import {
  getAgreementFileStage,
  getMasterAgreementStage,
  MasterAgreementStage,
} from "utils/master-agreement-signing";

export interface OrderContractHintProps {
  /** `orderEntity.customer.finance`: the finance record of the order's year. */
  finance?: CustomerFinance | string | null;
  /**
   * `orderEntity.customer.masterAgreementFileId`: the agreement file the order
   * page already has. Used when the finance record does not carry it (or is
   * only a bare id), so the hint does not claim there is no document.
   */
  agreementFile?: FileEntity | string | null;
  orderCreatedAt?: string | number | null;
  companyId?: string;
  customerId?: string;
}

const STAGE_MESSAGE_KEYS: Record<MasterAgreementStage, string> = {
  no_finance: "order_contract_no_finance",
  not_approved: "order_contract_not_approved",
  no_document: "order_contract_no_document",
  withdrawn: "order_contract_withdrawn",
  awaiting_client: "order_contract_awaiting_client",
  awaiting_company: "order_contract_awaiting_company",
  awaiting_signatures: "order_contract_awaiting_signatures",
  // An order only shows this hint while the agreement is not signed, so a
  // signed document here is inconsistent data — keep the generic wording.
  signed: "finance_document_should_be_sign",
};

/**
 * Tells the person looking at an order why it has no documents yet: which
 * step of the customer's master agreement is missing and where to do it.
 */
export default function OrderContractHint({
  finance,
  agreementFile,
  orderCreatedAt,
  companyId,
  customerId,
}: OrderContractHintProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const orderYear = new Date(orderCreatedAt ?? Date.now()).getFullYear();

  let stage: MasterAgreementStage;
  let financeId: string | undefined;
  let year = orderYear;
  if (typeof finance === "object" && finance !== null) {
    // The finance record is the source of truth for the file; the order's own
    // copy only fills in when the record does not carry a populated one.
    const financeFile = finance.masterAgreementFileId;
    stage = getMasterAgreementStage(
      finance,
      typeof financeFile === "object" && financeFile !== null ? financeFile : agreementFile
    );
    financeId = finance.id;
    year = finance.year || orderYear;
  } else if (typeof finance === "string" && finance) {
    // A bare finance id: the approval cannot be read, but the agreement file
    // can, and the id is enough to open the agreement page.
    stage = getAgreementFileStage(agreementFile);
    financeId = finance;
  } else {
    stage = "no_finance";
  }

  let action: { label: string; onClick: () => void } | undefined;
  if (companyId && customerId) {
    const customerUrl = `/app/${companyId}/customers/${customerId}`;
    action = financeId
      ? {
          label: t("order_open_master_agreement"),
          onClick: () => navigate(`${customerUrl}/finance/${year}/${financeId}`),
        }
      : {
          label: t("order_open_customer_card"),
          onClick: () => navigate(customerUrl),
        };
  }

  return <SigningHint action={action}>{t(STAGE_MESSAGE_KEYS[stage], { year })}</SigningHint>;
}
